
import { SuperbetRouteConfig } from '@superbet-group/web.types';

import { Product } from '@app/router/products/Product';
import { Link } from '@app/router/constants/Link';
import { RouteName } from '@app/router/constants/RouteName';
import { Title } from '@app/router/constants/Title';
import {
    defaultLayout,
    getOfferEventClasses,
    getSportPageClasses,
} from '@app/router/utils';
import { AnalyticsScreenNames } from '@app/utils/analyticsScreenNames';
import { favouritesPage } from '@pages/FavouritesPage';
import { prematchPage } from '@pages/SportsPage';
import { superAdvantagePage } from '@pages/SuperAdvantagePage';
import { multiEventBetBuilderPage } from '@pages/MultiEventBetBuilderPage';
import { eventDetailsPage } from '@app/modules/eventDetailsPage';
import SportCompetitionsPage from '@pages/SportCompetitionsPage/SportCompetitionsPage.vue';
import {
    capitalizeAll,
    isValidDate,
    toDate,
} from '@superbet-group/web.lib.utilities';
import { topWinnersPage } from '@pages/TopWinnersPage';
import { Route } from 'vue-router';
import { getTimeFilterFromSlug } from '@shared/timeFilter/helpers';
import { TimeFilter } from '@shared/timeFilter/types';
import SportsSocialMicroFeed from '@pages/SportsPage/SportsSocialMicroFeed.vue';
import { priceBoostPage } from '@pages/PriceBoostPage';

export const sportRoutes: SuperbetRouteConfig[] = [
    {
        path: Link.Sport.OfferEvent,
        name: RouteName.Sport.OfferEvent,
        isAccessible: Product.Sport,
        components: {
            ...defaultLayout,
            ...eventDetailsPage,
        },
        meta: {
            layoutClass: getOfferEventClasses,
            disableIndexing: (_route: Route) => true,
        },
    },
    {
        path: Link.Sport.PrematchOffer,
        name: RouteName.Sport.PrematchOffer,
        isAccessible: Product.Sport,
        components: {
            ...defaultLayout,
            ...prematchPage,
        },
        meta: {
            title: Title.Sport.CategoryPerTime,
            layoutClass: getSportPageClasses,
            superAnalytics: {
                pageName: AnalyticsScreenNames.preMatchSportOffer,
            },
        },
    },
    {
        path: Link.Sport.SportCompetitions,
        name: RouteName.Sport.SportCompetitions,
        isAccessible: Product.Sport,
        components: {
            ...defaultLayout,
            ...prematchPage,
            content: SportCompetitionsPage,
        },
        meta: {
            title: Title.Sport.SportCompetitions,
            layoutClass: getSportPageClasses,
            superAnalytics: {
                pageName: AnalyticsScreenNames.preMatchSportOffer,
            },
            mobileHeaderConfig: {
                isTopLevelRoute: true,
            },
        },
    },
    {
        path: Link.Sport.BetSlip,
        name: RouteName.Sport.BetSlip,
        isAccessible: Product.Sport,
        meta: {
            superAnalytics: {
                pageName: AnalyticsScreenNames.preMatchSportOffer,
            },
        },
    },
    // must be defined before category
    {
        path: Link.Sport.Social,
        name: RouteName.Sport.Social,
        isAccessible: Product.Sport,
        components: {
            ...defaultLayout,
            ...prematchPage,
            content: SportsSocialMicroFeed,
        },
        meta: {
            title: Title.Sport.Social,
            layoutClass: getSportPageClasses,
        },
    },
    // must be set before the next route
    {
        path: Link.Sport.CategoryPerTime,
        name: RouteName.Sport.CategoryPerTime,
        isAccessible: Product.Sport,
        components: {
            ...defaultLayout,
            ...prematchPage,
        },
        meta: {
            title: Title.Sport.CategoryPerTime,
            layoutClass: getSportPageClasses,
            superAnalytics: {
                pageName: AnalyticsScreenNames.sportOffer,
            },
            mobileHeaderConfig: {
                isTopLevelRoute: true,
            },
            disableIndexing: (route: Route) => {
                const timeFilterSlug = route.params?.timeFilter;
                if (timeFilterSlug) {
                    const timeFilter = getTimeFilterFromSlug(timeFilterSlug);
                    return isValidDate(toDate(timeFilterSlug))
                        || (timeFilter !== TimeFilter.Today && timeFilter !== TimeFilter.Live);
                }
                return false;
            },
        },
    },
    {
        path: Link.Sport.Category,
        name: RouteName.Sport.Category,
        isAccessible: Product.Sport,
        components: {
            ...defaultLayout,
            ...prematchPage,
        },
        meta: {
            title: Title.Sport.Category,
            layoutClass: getSportPageClasses,
            superAnalytics: {
                pageName: AnalyticsScreenNames.preMatchSportOffer,
            },
        },
    },
    {
        path: Link.Sport.Favourites,
        name: RouteName.Sport.Favourites,
        isAccessible: Product.Sport,
        components: {
            ...defaultLayout,
            ...favouritesPage,
        },
        meta: {
            title: Title.Sport.Favourites,
            layoutClass: 'sports-page favourites',
            superAnalytics: {
                pageName: AnalyticsScreenNames.preMatchSportOffer,
            },
            headerTitle: 'headerTitle.favourites',
        },
    },
    {
        path: Link.Sport.SuperAdvantage,
        name: RouteName.Sport.SuperAdvantage,
        isAccessible: Product.Sport,
        components: {
            ...defaultLayout,
            ...superAdvantagePage,
        },
        meta: {
            title: capitalizeAll(Title.Sport.SuperAdvantage),
            layoutClass: 'sports-page super-advantage-layout',
            headerTitle: 'super advantage',
        },
    },
    {
        path: Link.Sport.PriceBoost,
        name: RouteName.Sport.PriceBoost,
        isAccessible: Product.Sport,
        components: {
            ...defaultLayout,
            ...priceBoostPage,
        },
        meta: {
            title: capitalizeAll(Title.Sport.PriceBoost),
            layoutClass: 'sports-page price-boost-layout',
            headerTitle: 'generosity.priceboost.title',
        },
    },
    {
        path: Link.Sport.MultiEventBetBuilder,
        name: RouteName.Sport.MultiEventBetBuilder,
        isAccessible: Product.Sport,
        components: {
            ...defaultLayout,
            ...multiEventBetBuilderPage,
        },
        meta: {
            title: capitalizeAll(Title.Sport.MultiEventBetBuilder),
            layoutClass: 'sports-page multi-event-bet-builder-layout',
            headerTitle: 'betBuilder.multiEvent.page.title',
        },
    },
    {
        path: Link.Sport.TopWinners,
        name: RouteName.Sport.TopWinners,
        isAccessible: Product.Sport,
        components: {
            ...defaultLayout,
            ...topWinnersPage,
        },
        meta: {
            title: capitalizeAll(Title.Sport.TopWinners),
        },
    },
];
